import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ZoomIn } from 'lucide-react';

interface ImageGalleryProps {
  images: string[];
  imageNames?: string[];
  title?: string;
  maxColumns?: number;
  showDownload?: boolean;
  className?: string;
}

interface LightboxProps {
  images: string[];
  imageNames?: string[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  showDownload?: boolean;
}

const Lightbox: React.FC<LightboxProps> = ({
  images,
  imageNames,
  currentIndex,
  onClose,
  onNext,
  onPrev,
  showDownload = true,
}) => {
  const [isZoomed, setIsZoomed] = useState(false);

  const handleDownload = (url: string, fileName?: string) => {
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'image.jpg';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch((err) => console.error('Error downloading image:', err));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 p-2"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Navigation Buttons */}
      {images.length > 1 && (
        <>
          <button
            onClick={onPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-10 p-2 bg-black bg-opacity-50 rounded-full"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={onNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-10 p-2 bg-black bg-opacity-50 rounded-full"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Image Container */}
      <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4">
        <img
          src={images[currentIndex]}
          alt={imageNames?.[currentIndex] || `Image ${currentIndex + 1}`}
          className={`max-w-full max-h-full object-contain transition-transform ${
            isZoomed ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in'
          }`}
          onClick={() => setIsZoomed(!isZoomed)}
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage not found%3C/text%3E%3C/svg%3E';
          }}
        />
      </div>

      {/* Image Info Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-4 flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {imageNames?.[currentIndex] || `Image ${currentIndex + 1}`}
          </p>
          <p className="text-xs text-gray-300">
            {currentIndex + 1} of {images.length}
          </p>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={() => setIsZoomed(!isZoomed)}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded"
            title={isZoomed ? 'Zoom out' : 'Zoom in'}
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          {showDownload && (
            <button
              onClick={() => handleDownload(images[currentIndex], imageNames?.[currentIndex])}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded"
              title="Download image"
            >
              <Download className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  imageNames,
  title,
  maxColumns = 3,
  showDownload = false,
  className = '',
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!images || images.length === 0) {
    return null;
  }

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Handle keyboard navigation
  React.useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  const gridCols = `grid-cols-${Math.min(maxColumns, images.length)}`;
  const actualGridCols = images.length === 1 ? 'grid-cols-1' : 
                         images.length === 2 ? 'grid-cols-2' :
                         `grid-cols-${Math.min(maxColumns, 3)}`;

  return (
    <>
      <div className={className}>
        {title && (
          <h3 className="text-sm font-medium text-gray-700 mb-2">{title}</h3>
        )}
        <div className={`grid ${actualGridCols} gap-2`}>
          {images.map((url, index) => (
            <div
              key={index}
              className="relative group cursor-pointer"
              onClick={() => openLightbox(index)}
            >
              <img
                src={url}
                alt={imageNames?.[index] || `Image ${index + 1}`}
                className="w-full h-32 object-cover rounded border border-gray-200 hover:border-blue-400 transition-colors"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect width="200" height="150" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage not found%3C/text%3E%3C/svg%3E';
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded flex items-center justify-center">
                <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              {imageNames?.[index] && (
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate rounded-b">
                  {imageNames[index]}
                </div>
              )}
            </div>
          ))}
        </div>
        {images.length > maxColumns && (
          <p className="text-xs text-gray-500 mt-2">
            Click any image to view full size ({images.length} total)
          </p>
        )}
      </div>

      {lightboxOpen && (
        <Lightbox
          images={images}
          imageNames={imageNames}
          currentIndex={currentImageIndex}
          onClose={closeLightbox}
          onNext={nextImage}
          onPrev={prevImage}
          showDownload={showDownload}
        />
      )}
    </>
  );
};

export default ImageGallery;

